import { H2 } from '@/common/ui/h2'
import { getAddressString } from '@/common/utils/utils'
import { Dialog, DialogContent } from '@/common/ui/dialog'
import { useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { AccountInfoItem } from './AccountInfoItem'
import { Button } from '@/common/ui/button'
import { X } from 'lucide-react'
import ResetPassword from '@/auth/resetPassword'
import useAuthStore from '@/auth/authStore'
import { resetPassword, type ResetPasswordOutput } from 'aws-amplify/auth'
import { toast } from 'sonner'
import { useNavigate } from 'react-router'
import { ContactSupport } from './contactSupport'
import { ContactSupportModal } from './contactSupportModal'
import { useFetchTradingAccount } from '@/trading/tradingQueries'
import { AccountDetails } from './accountDetails'
import performManualSignOut from '@/auth/performManualSignOut'

export default function AccountSettings() {
    const { user } = useUserStore()
    const account = useCurrentAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const setUsername = useAuthStore((state) => state.setUsername)
    const { authDetails } = useAuthStore()

    // If user logged in via Google, username would be something like Google_12XXX7X99
    // If user logged in via Email, and password, username would be something like username@email.com
    // So we are checking that if username Starts with Google,
    // and we are also checking if it does not includes @ to avoid considering emails like googleuser@email.com as Google sign in
    const signedInViaGoogle = authDetails
        ? authDetails.username.toLowerCase().startsWith('google') && !authDetails.username.includes('@')
        : false

    const { data: tradingAccount } = useFetchTradingAccount(activeAccountId)
    const hasApprovedTradingAccount = tradingAccount?.status === 'APPROVED'
    const [isContactSupportOpen, setIsContactSupportOpen] = useState(false)
    const [isPasswordChangeOpen, setIsPasswordChangeOpen] = useState(false)
    const [isResettingPassword, setIsResettingPassword] = useState(false)
    const [sendingConfirmation, setSendingConfirmation] = useState(false)

    const navigate = useNavigate()

    const passwordChangeAlertRef = useRef(null)
    useClickAway(passwordChangeAlertRef, () => {
        handlePasswordModalClose()
    })

    const passwordResetModalRef = useRef(null)
    useClickAway(passwordResetModalRef, () => {
        handlePasswordModalClose()
    })

    const handlePasswordModalClose = () => {
        setIsPasswordChangeOpen(false)
    }

    const handlePasswordResetModalClose = () => {
        setIsResettingPassword(false)
    }

    const openResetPasswordForm = async () => {
        if (!user || !user.emailId) {
            toast.error('Email ID does not exist')
            return
        }
        setUsername(user.emailId)
        setSendingConfirmation(true)
        const output = await resetPassword({ username: user.emailId })
        handleResetPasswordNextSteps(output)
        setSendingConfirmation(false)
        setIsPasswordChangeOpen(false)
        setIsResettingPassword(true)
    }

    function handleResetPasswordNextSteps(output: ResetPasswordOutput) {
        const { nextStep } = output
        const codeDeliveryDetails = nextStep.codeDeliveryDetails
        switch (nextStep.resetPasswordStep) {
            case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
                toast.success(`Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium?.toLowerCase()}`)
                // Collect the confirmation code from the user and pass to confirmResetPassword.
                break
            case 'DONE':
                break
        }
    }

    async function handleReset() {
        await performManualSignOut({ redirect: true })
        toast.success('You have reset the password successfully. Please login again!')
        navigate('/')
    }

    if (!user) return null

    return (
        <div>
            {hasApprovedTradingAccount && <AccountDetails tradingAccount={tradingAccount} account={account} />}

            <H2 className="mt-5">Personal details</H2>
            <ul className="mt-2 mb-16">
                {user.firstName && user.lastName && (
                    <AccountInfoItem
                        title="Name"
                        info={`${user.firstName} ${user.middleName ? `${user.middleName} ` : ``}${user.lastName}`}
                    />
                )}
                <AccountInfoItem title="Email" info={user.emailId} />
                {user.mobile && <AccountInfoItem title="Mobile Number" info={user.mobile} />}
                {user.addresses?.residentialAddress && (
                    <AccountInfoItem title="Address" info={getAddressString(user.addresses?.residentialAddress)} />
                )}
                {user.addresses?.postalAddress && (
                    <AccountInfoItem title="Postal Address" info={getAddressString(user.addresses?.postalAddress)} />
                )}
                {!signedInViaGoogle && (
                    <AccountInfoItem
                        title="Password"
                        info={'•'.repeat(12)}
                        editHandler={() => setIsPasswordChangeOpen(true)}
                    />
                )}
            </ul>

            {isPasswordChangeOpen && (
                <Dialog open={isPasswordChangeOpen}>
                    <div className="py-10 h-screen">
                        <DialogContent
                            ref={passwordChangeAlertRef}
                            onClose={handlePasswordModalClose}
                            className="px-5 py-6 max-h-[90vh] w-[94vw] max-w-[680px] overflow-y-auto xs:p-20"
                        >
                            <H2 className="text-center">Change your password</H2>
                            <p className="text-center py-5">
                                In order to change your password, You must have access to your email. Do you wish to
                                continue?
                            </p>
                            <div className="flex justify-between">
                                <Button
                                    variant="secondary"
                                    onClick={openResetPasswordForm}
                                    loading={sendingConfirmation}
                                >
                                    Yes, I have access to my email
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="hover:bg-transparent"
                                    onClick={handlePasswordModalClose}
                                >
                                    <div className="bg-accent rounded-full p-1">
                                        <X className="h-5 w-5" />
                                    </div>
                                    Cancel
                                </Button>
                            </div>
                        </DialogContent>
                    </div>
                </Dialog>
            )}

            {isResettingPassword && (
                <Dialog open={isResettingPassword}>
                    <div className="py-10 h-screen">
                        <DialogContent
                            className="px-5 py-12 max-h-[90vh] w-[94vw] max-w-[680px] overflow-y-auto xs:p-20"
                            onClose={handlePasswordResetModalClose}
                        >
                            <ResetPassword onReset={handleReset} />
                        </DialogContent>
                    </div>
                </Dialog>
            )}
            <ContactSupport showModal={() => setIsContactSupportOpen(true)} />
            {isContactSupportOpen && <ContactSupportModal closeModal={() => setIsContactSupportOpen(false)} />}
        </div>
    )
}
